import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Save, X, Clock, Edit2, Trash2, Copy, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function VagterTab({ token, eventId, eventName, onCountChange, showConfirm, closeConfirm }) {
  const [shifts, setShifts] = useState([]);
  const [showAddShiftForm, setShowAddShiftForm] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [newShift, setNewShift] = useState({ date: "", start_time: "09:00", end_time: "15:00", name: "" });
  // Kopier-modal: kan rumme én vagt eller alle vagter for en dag
  const [copyDialog, setCopyDialog] = useState({ open: false, sourceShifts: [], sourceLabel: '' });
  const [copyTargetDates, setCopyTargetDates] = useState([""]);
  const [copyBusy, setCopyBusy] = useState(false);
  // Sammenklap/udvid pr. dag (default: alle dage er sammenklappede)
  const [expandedDates, setExpandedDates] = useState(() => new Set());
  const toggleDate = (date) => setExpandedDates(prev => {
    const next = new Set(prev);
    if (next.has(date)) next.delete(date); else next.add(date);
    return next;
  });
  const expandAll = (dates) => setExpandedDates(new Set(dates));
  const collapseAll = () => setExpandedDates(new Set());

  const loadShifts = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/shifts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || [];
      setShifts(data);
      if (onCountChange) onCountChange(data.length);
    } catch (error) {
      console.error("Fejl ved hentning af vagter", error);
    }
  };

  useEffect(() => { loadShifts(); }, [eventId]);

  const saveShift = async () => {
    if (!newShift.date || !newShift.start_time || !newShift.end_time) {
      toast.error("Udfyld dato, starttid og sluttid");
      return;
    }
    try {
      if (editingShift) {
        await axios.put(`${API}/events/${eventId}/shifts/${editingShift.id}`, newShift, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Vagt opdateret!");
      } else {
        await axios.post(`${API}/events/${eventId}/shifts`, newShift, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Vagt oprettet!");
      }
      setShowAddShiftForm(false);
      setEditingShift(null);
      setNewShift({ date: "", start_time: "09:00", end_time: "15:00", name: "" });
      loadShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved gem");
    }
  };

  const deleteShift = (shift) => {
    showConfirm(
      'Slet vagt',
      `Er du sikker på at du vil slette vagten ${shift.start_time} - ${shift.end_time}?`,
      async () => {
        try {
          await axios.delete(`${API}/events/${eventId}/shifts/${shift.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Vagt slettet");
          loadShifts();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Fejl ved sletning");
        }
        closeConfirm();
      }
    );
  };

  const openCopyDialog = (sourceShifts, sourceLabel) => {
    setCopyDialog({ open: true, sourceShifts, sourceLabel });
    setCopyTargetDates([""]);
  };
  const closeCopyDialog = () => {
    setCopyDialog({ open: false, sourceShifts: [], sourceLabel: '' });
    setCopyTargetDates([""]);
  };
  const addCopyTargetDate = () => setCopyTargetDates(prev => [...prev, ""]);
  const setCopyTargetDate = (i, val) => setCopyTargetDates(prev => prev.map((d, idx) => idx === i ? val : d));
  const removeCopyTargetDate = (i) => setCopyTargetDates(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  const executeCopy = async () => {
    const validDates = [...new Set(copyTargetDates.map(d => (d || '').trim()).filter(Boolean))];
    if (validDates.length === 0) { toast.error("Vælg mindst én destinationsdato"); return; }
    setCopyBusy(true);
    let okCount = 0, errCount = 0;
    try {
      for (const date of validDates) {
        for (const src of copyDialog.sourceShifts) {
          // Spring over hvis vagten allerede ligger på dén dato med samme tider
          const conflict = shifts.find(s => s.date === date && s.start_time === src.start_time && s.end_time === src.end_time);
          if (conflict) continue;
          try {
            await axios.post(`${API}/events/${eventId}/shifts`, {
              date,
              start_time: src.start_time,
              end_time: src.end_time,
              name: src.name || ""
            }, { headers: { Authorization: `Bearer ${token}` } });
            okCount++;
          } catch {
            errCount++;
          }
        }
      }
      if (okCount > 0) toast.success(`${okCount} ${okCount === 1 ? 'vagt' : 'vagter'} kopieret`);
      if (okCount === 0 && errCount === 0) toast.info("Ingen nye vagter — alle eksisterede allerede");
      if (errCount > 0) toast.error(`${errCount} vagt(er) kunne ikke kopieres`);
      closeCopyDialog();
      loadShifts();
    } finally {
      setCopyBusy(false);
    }
  };

  return (
    <div className="space-y-4 relative">
      {/* Sticky toolbar — header + redigerings-form bliver der mens du scroller (lige under Stamdata-headeren) */}
      <div className="sticky z-10 -mx-4 px-4 pt-2 pb-3 bg-background border-b border-border space-y-3" style={{ top: '130px' }}>
        <div className="flex items-center justify-between gap-2">
          <div>
            <h2 className="text-xl font-bold">Vagter</h2>
            <p className="text-sm text-muted-foreground">Opret og administrer vagter for {eventName}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {shifts.length > 0 && (
              <>
                <Button variant="outline" size="sm" onClick={() => expandAll(Array.from(new Set(shifts.map(s => s.date))))} title="Udvid alle dage">Udvid alle</Button>
                <Button variant="outline" size="sm" onClick={collapseAll} title="Klap alle dage sammen">Klap alle</Button>
              </>
            )}
            <Button onClick={() => { setEditingShift(null); setNewShift({ date: "", start_time: "09:00", end_time: "15:00", name: "" }); setShowAddShiftForm(true); }} data-testid="add-shift-btn">
              <Plus className="h-4 w-4 mr-2" />Tilføj vagt
            </Button>
          </div>
        </div>

        {showAddShiftForm && (
          <div className="bg-card border border-border rounded-lg px-3 py-2 flex flex-wrap items-end gap-2">
            <div className="flex-shrink-0">
              <Label className="text-[10px] uppercase text-muted-foreground tracking-wide">Dato</Label>
              <Input type="date" value={newShift.date} onChange={(e) => setNewShift({ ...newShift, date: e.target.value })} className="h-8 w-[150px]" autoFocus />
            </div>
            <div className="flex-shrink-0">
              <Label className="text-[10px] uppercase text-muted-foreground tracking-wide">Start</Label>
              <Input type="time" value={newShift.start_time} onChange={(e) => setNewShift({ ...newShift, start_time: e.target.value })} className="h-8 w-[100px]" />
            </div>
            <div className="flex-shrink-0">
              <Label className="text-[10px] uppercase text-muted-foreground tracking-wide">Slut</Label>
              <Input type="time" value={newShift.end_time} onChange={(e) => setNewShift({ ...newShift, end_time: e.target.value })} className="h-8 w-[100px]" />
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="text-[10px] uppercase text-muted-foreground tracking-wide">Navn (valgfrit)</Label>
              <Input value={newShift.name} onChange={(e) => setNewShift({ ...newShift, name: e.target.value })} placeholder="fx Opbygning" className="h-8" onKeyDown={(e) => { if (e.key === 'Enter') saveShift(); }} />
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={() => { setShowAddShiftForm(false); setEditingShift(null); }} className="h-8"><X className="h-3.5 w-3.5 mr-1" />Annuller</Button>
              <Button size="sm" onClick={saveShift} className="h-8"><Save className="h-3.5 w-3.5 mr-1" />{editingShift ? 'Opdater' : 'Gem'}</Button>
            </div>
          </div>
        )}
      </div>

      {shifts.length === 0 ? (
        <div className="text-center py-12 bg-card/30 rounded-lg border border-dashed border-border">
          <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Ingen vagter oprettet</h3>
          <p className="text-muted-foreground mb-4">Opret vagter for at frivillige kan tilmelde sig</p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(
            shifts.reduce((acc, shift) => { const date = shift.date; if (!acc[date]) acc[date] = []; acc[date].push(shift); return acc; }, {})
          ).sort(([a], [b]) => a.localeCompare(b)).map(([date, dateShifts]) => {
            const dateLabel = new Date(date + 'T00:00:00').toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
            const isExpanded = expandedDates.has(date);
            return (
              <div key={date} className="bg-card border border-border rounded-lg overflow-hidden">
                {/* Sammenklappelig dag-header */}
                <div className="px-3 py-2 bg-muted/30 border-b border-border flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleDate(date)}
                    className="flex-1 flex items-center gap-2 text-left min-w-0"
                    data-testid={`day-toggle-${date}`}
                  >
                    <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    <h3 className="font-bold truncate">{dateLabel}</h3>
                    <span className="text-xs text-muted-foreground flex-shrink-0">· {dateShifts.length} {dateShifts.length === 1 ? 'vagt' : 'vagter'}</span>
                  </button>
                  {dateShifts.length > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openCopyDialog(dateShifts, `alle ${dateShifts.length} vagter fra ${dateLabel}`); }}
                      data-testid={`copy-day-${date}`}
                      title="Kopiér alle vagter fra denne dag til andre datoer"
                    >
                      <Copy className="h-4 w-4 mr-1.5" />Kopiér alle
                    </Button>
                  )}
                </div>

                {isExpanded && (
                  <div className="divide-y divide-border">
                    {dateShifts.sort((a, b) => a.start_time.localeCompare(b.start_time)).map(shift => (
                      <div key={shift.id} className="p-4 flex items-center justify-between hover:bg-muted/20">
                        <div className="flex items-center gap-4">
                          <div className="text-2xl font-mono font-bold text-primary">{shift.start_time}</div>
                          <div className="text-muted-foreground">→</div>
                          <div className="text-2xl font-mono font-bold">{shift.end_time}</div>
                          {shift.name && <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">{shift.name}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openCopyDialog([shift], `vagten ${shift.start_time}–${shift.end_time}${shift.name ? ' ('+shift.name+')' : ''}`)}
                            title="Kopiér denne vagt til andre datoer"
                            data-testid={`copy-shift-${shift.id}`}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setEditingShift(shift); setNewShift({ date: shift.date, start_time: shift.start_time, end_time: shift.end_time, name: shift.name || "" }); setShowAddShiftForm(true); }}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => deleteShift(shift)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Kopiér-vagter modal */}
      {copyDialog.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeCopyDialog}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="copy-shift-dialog">
            <div>
              <h3 className="font-bold text-lg">Kopiér vagter</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Kopierer {copyDialog.sourceLabel} til de valgte datoer.</p>
            </div>

            <div className="bg-muted/30 rounded p-2 text-xs space-y-0.5 max-h-32 overflow-y-auto">
              {copyDialog.sourceShifts.map(s => (
                <div key={s.id} className="font-mono">
                  {s.start_time} → {s.end_time}{s.name ? ` · ${s.name}` : ''}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Destinationsdatoer</Label>
              {copyTargetDates.map((d, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    type="date"
                    value={d}
                    onChange={(e) => setCopyTargetDate(i, e.target.value)}
                    data-testid={`copy-target-date-${i}`}
                  />
                  {copyTargetDates.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeCopyTargetDate(i)} className="text-destructive flex-shrink-0">
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addCopyTargetDate} className="w-full" data-testid="copy-add-date-btn">
                <Plus className="h-4 w-4 mr-1.5" />Tilføj endnu en dato
              </Button>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={closeCopyDialog} disabled={copyBusy}>Annullér</Button>
              <Button onClick={executeCopy} disabled={copyBusy} data-testid="copy-execute-btn">
                <Copy className="h-4 w-4 mr-1.5" />{copyBusy ? 'Kopierer...' : 'Kopiér'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
